import { Color3, GroundMesh, Matrix, Mesh, MeshBuilder, Scene, Vector3 } from '@babylonjs/core';
import { CustomMaterial } from '@babylonjs/materials';

/**
 * Configuration for the rain builder.
 */
export interface RainBuilderConfig {
  /**
   * The height of the rain field in world units.
   */
  rainHeight: number;

  /**
   * The number of rows of rain. Creates the grid of rain.
   * The total number of drops will be rowSize * rowSize.
   * A value of 200 is a good starting point.
   */
  rowSize: number;

  /**
   * The number of rows of ripples.
   * The total number of ripples will be rippleRowSize * rippleRowSize.
   * A value of 80 is a good starting point.
   * 0 = no ripples
   */
  rippleRowSize: number;

  /**
   * The density of the rain grid. The total size of the grid will be rippleRowSize * density.
   * A value of 0.25 is a good starting point.
   */
  density: number;

  /**
   * The speed factor of the rain.
   * A value of 1 is a good starting point.
   */
  speedFactor: number;

  /**
   * The center of the rain field.
   */
  center: Vector3;

  /**
   * The ground mesh that the rain will fall on.
   */
  ground: GroundMesh;
}

/**
 * Creates a rain system that falls from the sky and creates ripples on the ground.
 * A grid of rain is created. The grid is generated around a center position.
 * If you have a moving center like a player, configure the center with the position reference of your player, to let the rain grid move with the player.
 * The rain drops shader works with relative positions to the center in local space.
 *
 * The ripple shader works with world space positions, since the ripples must not move with the camera.
 *
 *
 * Inspired by https://playground.babylonjs.com/#MXV7VQ#2
 *
 */
export class RainBuilder {
  private initTime: number = +new Date();
  constructor(public scene: Scene) {}

  public create(config: RainBuilderConfig) {
    // -----------------------------------------------------------------------
    // RIPPLE MESH  — world-space matrices, mesh stays at origin, smaller grid
    // -----------------------------------------------------------------------
    let rippleUpdate: (cx: number, cz: number) => void;
    if (config.rippleRowSize > 0) {
      const rippleMaterial = new CustomMaterial('rippleMaterial', this.scene);
      rippleMaterial.diffuseColor = Color3.FromHexString('#7799B1');
      rippleMaterial.specularColor = new Color3(0, 0, 0);
      rippleMaterial.emissiveColor = new Color3(0.175, 0.175, 0.175);
      rippleMaterial.alpha = 0.9;

      const rippleMesh = MeshBuilder.CreateTube(
        'ripple',
        {
          path: (() => {
            const pts = [];
            const size = 0.5;
            const segs = 12;
            const step = (Math.PI * 2) / segs;
            for (let i = 0; i <= Math.PI * 2 + 0.001; i += step) {
              pts.push(new Vector3(Math.cos(i) * size, 0, Math.sin(i) * size));
            }
            return pts;
          })(),
          radius: 0.01,
          tessellation: 3,
        },
        this.scene
      );
      rippleMesh.material = rippleMaterial;

      const rippleRowSize = config.rippleRowSize;
      const rippleCount = rippleRowSize * rippleRowSize;
      // The total side length of the ripple grid in world units.
      const rippleRange = rippleRowSize * config.density;
      const rippleHalfRange = rippleRange / 2;

      const rippleMatrices = new Float32Array(16 * rippleCount);
      const rippleIdx = new Float32Array(rippleCount);
      const rippleRFactor1 = new Float32Array(rippleCount);
      // Absolute world positions per instance — these never shift with the camera.
      // They only change when an instance wraps around to the opposite edge.
      const rippleWorldX = new Float32Array(rippleCount);
      const rippleWorldZ = new Float32Array(rippleCount);

      {
        let n = 0;
        for (let x = 0; x < rippleRowSize; x++) {
          for (let z = 0; z < rippleRowSize; z++) {
            const rx = x + (Math.random() - 0.5);
            const rz = z + (Math.random() - 0.5);
            const wx = config.center.x + (rx - rippleRowSize / 2) * config.density;
            const wz = config.center.z + (rz - rippleRowSize / 2) * config.density;
            const wy = config.ground.getHeightAtCoordinates(wx, wz);

            rippleWorldX[n] = wx;
            rippleWorldZ[n] = wz;

            // Pre-fill identity matrix; only the translation part changes on recycle.
            rippleMatrices[n * 16 + 0] = 1;
            rippleMatrices[n * 16 + 5] = 1;
            rippleMatrices[n * 16 + 10] = 1;
            rippleMatrices[n * 16 + 15] = 1;
            rippleMatrices[n * 16 + 12] = wx;
            rippleMatrices[n * 16 + 13] = wy;
            rippleMatrices[n * 16 + 14] = wz;

            rippleIdx[n] = n;
            rippleRFactor1[n] = Math.random();
            n++;
          }
        }
      }

      rippleMesh.thinInstanceSetBuffer('matrix', rippleMatrices, 16);
      rippleMesh.thinInstanceSetBuffer('idx', rippleIdx, 1);
      rippleMesh.thinInstanceSetBuffer('rFactor1', rippleRFactor1, 1);

      rippleMaterial.Vertex_Definitions(`
attribute float idx;
attribute float rFactor1;

varying float vR;
    `);
      // Ripples are already in world space via their matrix (Y = groundY baked in).
      // No groundY attribute needed here.
      rippleMaterial.Vertex_Before_PositionUpdated(`
float t = iTime * speedFactor;
float s = fract(t + rFactor1);
float ps = .1;
float ss = clamp(s, 0., ps);
float r = ss / ps;

vR = r;
positionUpdated.xz *= r;
positionUpdated.y *= (1. - r) * 2.;
    `);

      rippleMaterial.Fragment_Definitions(`
varying float vR;
    `);
      rippleMaterial.Fragment_Before_FragColor(`
color.a *= 1. - vR;
    `);

      rippleMaterial.AddUniform('iTime', 'float', undefined);
      rippleMaterial.AddUniform('speedFactor', 'float', undefined);
      rippleMaterial.AddUniform('rainHeight', 'float', undefined);

      rippleMaterial.onBind = () => {
        const time = (+new Date() - this.initTime) * 0.001;
        rippleMaterial.getEffect().setFloat('iTime', time);
        rippleMaterial.getEffect().setFloat('speedFactor', config.speedFactor);
        rippleMaterial.getEffect().setFloat('rainHeight', config.rainHeight);
      };

      rippleMaterial.AddAttribute('rFactor1');
      rippleMaterial.AddAttribute('idx');

      rippleUpdate = (cx: number, cz: number) => {
        // RIPPLES: world positions are fixed. Only instances that drifted out of
        // the grid range get recycled to the opposite edge (Pac-Man wrap).
        // Everything within range keeps its matrix untouched — no sliding.
        let ripplesDirty = false;
        for (let i = 0; i < rippleCount; i++) {
          let wx = rippleWorldX[i];
          let wz = rippleWorldZ[i];
          let recycled = false;

          // Wrap X
          if (wx < cx - rippleHalfRange) {
            wx += rippleRange;
            recycled = true;
          } else if (wx > cx + rippleHalfRange) {
            wx -= rippleRange;
            recycled = true;
          }
          // Wrap Z
          if (wz < cz - rippleHalfRange) {
            wz += rippleRange;
            recycled = true;
          } else if (wz > cz + rippleHalfRange) {
            wz -= rippleRange;
            recycled = true;
          }

          if (recycled) {
            rippleWorldX[i] = wx;
            rippleWorldZ[i] = wz;
            const wy = config.ground.getHeightAtCoordinates(wx, wz);
            rippleMatrices[i * 16 + 12] = wx;
            rippleMatrices[i * 16 + 13] = wy;
            rippleMatrices[i * 16 + 14] = wz;
            ripplesDirty = true;
          }
        }
        // Only upload the matrix buffer when at least one instance was recycled.
        if (ripplesDirty) {
          rippleMesh.thinInstanceSetBuffer('matrix', rippleMatrices, 16);
        }
      };
    }

    // -----------------------------------------------------------------------
    // DROP MESH  — local-space, mesh follows center in XZ, groundY attribute
    // -----------------------------------------------------------------------

    const dropMaterial = new CustomMaterial('dropMaterial', this.scene);
    dropMaterial.specularColor = Color3.FromHexString('#7799B1');
    dropMaterial.emissiveColor = new Color3(0.175, 0.175, 0.175);
    dropMaterial.alpha = 0.8;

    const dropPath = [];
    const dropSegments = 2;
    for (let i = 0; i < 1; i += 1 / dropSegments) {
      dropPath.push(new Vector3(0, i, 0));
    }
    const dropMesh = MeshBuilder.CreateTube(
      'drop',
      {
        path: dropPath,
        tessellation: 3,
        radiusFunction: (i) => {
          const maxRadius = 0.0125;
          const f = 1 - (i + 1) / dropSegments;
          return maxRadius * f;
        },
        cap: Mesh.CAP_START,
        updatable: false,
        sideOrientation: Mesh.DEFAULTSIDE,
      },
      this.scene
    );
    dropMesh.material = dropMaterial;

    const dropCount = config.rowSize * config.rowSize;
    const dropMatrices = new Float32Array(16 * dropCount);
    const dropIdx = new Float32Array(dropCount);
    const dropRFactor1 = new Float32Array(dropCount);
    const dropLocalOffsets = new Float32Array(dropCount * 2);
    const dropGroundY = new Float32Array(dropCount);

    {
      let n = 0;
      for (let x = 0; x < config.rowSize; x++) {
        for (let z = 0; z < config.rowSize; z++) {
          const rx = x + (Math.random() - 0.5);
          const rz = z + (Math.random() - 0.5);
          const lx = (rx - config.rowSize / 2) * config.density;
          const lz = (rz - config.rowSize / 2) * config.density;
          dropLocalOffsets[n * 2 + 0] = lx;
          dropLocalOffsets[n * 2 + 1] = lz;

          const wx = config.center.x + lx;
          const wz = config.center.z + lz;
          dropGroundY[n] = config.ground.getHeightAtCoordinates(wx, wz);

          const matrix = Matrix.Translation(lx, 0, lz);
          matrix.copyToArray(dropMatrices, n * 16);
          dropIdx[n] = n;
          dropRFactor1[n] = Math.random();
          n++;
        }
      }
    }

    dropMesh.thinInstanceSetBuffer('matrix', dropMatrices, 16);
    dropMesh.thinInstanceSetBuffer('idx', dropIdx, 1);
    dropMesh.thinInstanceSetBuffer('rFactor1', dropRFactor1, 1);
    dropMesh.thinInstanceSetBuffer('groundY', dropGroundY, 1);

    dropMaterial.Vertex_Definitions(`
attribute float idx;
attribute float rFactor1;
attribute float groundY;
    `);
    dropMaterial.Vertex_Before_PositionUpdated(`
float t = iTime * speedFactor;
positionUpdated.y -= (fract(t + rFactor1) - 1.0) * rainHeight;
positionUpdated.y += groundY;
    `);

    dropMaterial.AddUniform('iTime', 'float', undefined);
    dropMaterial.AddUniform('speedFactor', 'float', undefined);
    dropMaterial.AddUniform('rainHeight', 'float', undefined);
    dropMaterial.onBind = () => {
      const time = (+new Date() - this.initTime) * 0.001;
      dropMaterial.getEffect().setFloat('iTime', time);
      dropMaterial.getEffect().setFloat('speedFactor', config.speedFactor);
      dropMaterial.getEffect().setFloat('rainHeight', config.rainHeight);
    };
    dropMaterial.AddAttribute('rFactor1');
    dropMaterial.AddAttribute('idx');
    dropMaterial.AddAttribute('groundY');

    // -----------------------------------------------------------------------
    // PER-FRAME UPDATE
    // -----------------------------------------------------------------------

    this.scene.onBeforeRenderObservable.add(() => {
      const cx = config.center.x;
      const cz = config.center.z;

      // DROPS: mesh follows player in XZ (Y always 0), update groundY attribute
      dropMesh.position.x = cx;
      dropMesh.position.z = cz;
      dropMesh.position.y = 0;

      for (let i = 0; i < dropCount; i++) {
        dropGroundY[i] = config.ground.getHeightAtCoordinates(cx + dropLocalOffsets[i * 2 + 0], cz + dropLocalOffsets[i * 2 + 1]);
      }
      dropMesh.thinInstanceSetBuffer('groundY', dropGroundY, 1);

      if (rippleUpdate) {
        rippleUpdate(cx, cz);
      }
    });
  }
}
